import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  login as apiLogin,
  autologin,
  refreshToken,
  logout as apiLogout,
} from "../services/authApi";

const AuthContext = createContext(null);

/** Dedupe autologin across React StrictMode double-mount (dev) */
let pendingAutologin = null;

function isHomePath(pathname) {
  return pathname === "/" || pathname === "";
}

function restoreUserFromStorage() {
  const stored = localStorage.getItem("user");
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

function initialAuthStatus(pathname) {
  return restoreUserFromStorage()
    ? "authenticated"
    : "checking";
}

export function AuthProvider({ children }) {
  const location = useLocation();
  const syncIdRef = useRef(0);
  const [user, setUser] = useState(() => restoreUserFromStorage());
  const [authStatus, setAuthStatus] = useState(() =>
    initialAuthStatus(location.pathname),
  );

  const persistUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  }, []);

  const clearUser = useCallback(() => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  }, []);

  const runAutologin = useCallback(async () => {
    if (!pendingAutologin) {
      pendingAutologin = autologin().finally(() => {
        pendingAutologin = null;
      });
    }
    return pendingAutologin;
  }, []);

  /** Home (/): verify session via autologin; optimistic UI when profile is cached */
  const initHomeAuth = useCallback(
    async (syncId) => {
      const stored = restoreUserFromStorage();
      if (stored) {
        setUser(stored);
        setAuthStatus("authenticated");
      } else {
        setAuthStatus("checking");
      }

      try {
        const userData = await runAutologin();
        if (syncId !== syncIdRef.current) return userData;
        persistUser(userData);
        setAuthStatus("authenticated");
        return userData;
      } catch {
        if (syncId !== syncIdRef.current) return null;
        clearUser();
        setAuthStatus("unauthenticated");
        return null;
      }
    },
    [clearUser, persistUser, runAutologin],
  );

  /** Protected routes: refresh token; restore profile from storage or autologin */
  const initProtectedAuth = useCallback(
    async (syncId) => {
      const stored = restoreUserFromStorage();
      if (stored) {
        setUser(stored);
        setAuthStatus("authenticated");
      } else {
        setAuthStatus("checking");
      }

      const finishAuth = (userData) => {
        if (syncId !== syncIdRef.current) return userData;
        persistUser(userData);
        setAuthStatus("authenticated");
        return userData;
      };

      const failAuth = () => {
        if (syncId !== syncIdRef.current) return null;
        clearUser();
        setAuthStatus("unauthenticated");
        return null;
      };

      try {
        await refreshToken();

        let userData = restoreUserFromStorage();
        if (!userData) {
          userData = await runAutologin();
        }

        return finishAuth(userData);
      } catch {
        try {
          const userData = await runAutologin();
          return finishAuth(userData);
        } catch {
          return failAuth();
        }
      }
    },
    [clearUser, persistUser, runAutologin],
  );

  const syncAuthWithRoute = useCallback(async () => {
    const syncId = ++syncIdRef.current;

    if (isHomePath(location.pathname)) {
      return initHomeAuth(syncId);
    }

    return initProtectedAuth(syncId);
  }, [location.pathname, initHomeAuth, initProtectedAuth]);

  useEffect(() => {
    syncAuthWithRoute();
  }, [syncAuthWithRoute]);

  const login = useCallback(
    async (username, password) => {
      const userData = await apiLogin(username, password);
      persistUser(userData);
      setAuthStatus("authenticated");
      return userData;
    },
    [persistUser],
  );

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      /* clear local state even if API fails */
    }
    clearUser();
    setAuthStatus("unauthenticated");
  }, [clearUser]);

  const value = useMemo(
    () => ({
      user,
      authStatus,
      isAuthenticated: authStatus === "authenticated",
      isChecking: authStatus === "checking",
      isHomeRoute: isHomePath(location.pathname),
      login,
      logout,
      initHomeAuth,
      initProtectedAuth,
      syncAuthWithRoute,
    }),
    [
      user,
      authStatus,
      location.pathname,
      login,
      logout,
      initHomeAuth,
      initProtectedAuth,
      syncAuthWithRoute,
    ],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
